<template>
  <div class="materials-section">
    <table class="table table-sm materials-table">
      <thead>
        <tr>
          <th colspan="10" class="materials-heading">
            <span class="materials-title">Materials</span>
            <span class="materials-summary">
              <template v-if="user.materialEntries.length">
                {{ user.materialEntries.length }} {{ user.materialEntries.length === 1 ? 'entry' : 'entries' }}
                <template v-if="hasPricedEntries"> · {{ formatMoney(totalCost) }}</template>
              </template>
              <template v-else>None recorded this week</template>
            </span>
          </th>
        </tr>
      </thead>

      <tbody>
        <!-- Column labels sit in the body, like the hours grid, so they get its light styling. -->
        <tr v-if="user.materialEntries.length" class="column-labels">
          <th scope="col" class="col-date text-dark bg-light">Date</th>
          <th scope="col" class="col-job text-dark bg-light">Job</th>
          <th scope="col" class="col-part text-dark bg-light">Part</th>
          <th scope="col" class="col-material text-dark bg-light">Material</th>
          <th scope="col" class="col-qty text-dark bg-light text-right">Qty</th>
          <th scope="col" class="col-unit text-dark bg-light">Unit</th>
          <th scope="col" class="col-cost text-dark bg-light text-right">Unit cost</th>
          <th scope="col" class="col-total text-dark bg-light text-right">Total</th>
          <th scope="col" class="col-note text-dark bg-light">Note</th>
          <th scope="col" class="col-actions text-dark bg-light"></th>
        </tr>

        <tr v-for="(row, index) in user.materialEntries" :key="row.id || `new-${index}`" :class="{ locked: !row.editable }">
          <td>
            <select v-model="row.usedOn" class="form-control ms-input" :disabled="!row.editable" aria-label="Date used" @change="save(row)">
              <option v-for="day in weekDays" :key="day.value" :value="day.value">{{ day.label }}</option>
            </select>
          </td>

          <td>
            <job-select
              v-model="row.jobId"
              :jobs="jobs"
              :show-all-jobs="showAllJobs"
              :disabled="!row.editable"
              @input="row.partName = null; save(row)"
            />
          </td>

          <td>
            <v-select
              v-model="row.partName"
              :options="partOptions(row.jobId)"
              :disabled="!row.jobId || !row.editable"
              append-to-body
              placeholder="Optional"
              @input="save(row)"
            />
          </td>

          <td>
            <v-select
              :value="materialValue(row)"
              :options="catalogue"
              label="name"
              taggable
              :disabled="!row.editable"
              append-to-body
              placeholder="Pick or type a material"
              @input="setMaterial(row, $event)"
            >
              <template #no-options>Type a name to add a new material.</template>
            </v-select>
          </td>

          <td>
            <input
              v-model="row.quantity"
              type="number"
              min="0"
              step="any"
              class="form-control ms-input text-right"
              :disabled="!row.editable"
              aria-label="Quantity"
              @change="save(row)"
            />
          </td>

          <td>
            <v-select v-model="row.unit" :options="units" :clearable="false" :disabled="!row.editable" append-to-body @input="save(row)" />
          </td>

          <td>
            <div class="money-input">
              <span>$</span>
              <input
                v-model="row.unitCost"
                type="number"
                min="0"
                step="0.01"
                class="form-control ms-input text-right"
                :disabled="!row.editable"
                placeholder="—"
                aria-label="Unit cost"
                @change="save(row)"
              />
            </div>
          </td>

          <td class="text-right text-nowrap">{{ rowTotal(row) === null ? '—' : formatMoney(rowTotal(row)) }}</td>

          <td>
            <input
              v-model="row.note"
              type="text"
              class="form-control ms-input"
              :disabled="!row.editable"
              placeholder="Optional"
              aria-label="Note"
              @input="debouncedSave(row)"
            />
          </td>

          <td class="text-nowrap text-right">
            <span
              v-if="row.finalized"
              v-b-tooltip.hover.top="finalizedTitle(row)"
              class="finalized-flag"
              :class="{ locked: !row.editable }"
              :aria-label="finalizedTitle(row)"
            >
              <feather-icon icon="LockIcon" />
            </span>
            <span class="save-state" :class="row.saveState" :title="saveStateTitle(row)">
              <b-spinner v-if="row.saveState === 'saving'" small />
              <feather-icon v-else-if="row.saveState === 'saved'" icon="CheckIcon" />
              <feather-icon v-else-if="row.saveState === 'error'" icon="AlertCircleIcon" />
              <feather-icon v-else-if="row.saveState === 'incomplete'" icon="EditIcon" />
            </span>
            <b-button :disabled="!row.editable" class="btn btn-sm" variant="danger" aria-label="Delete material" @click="deleteEntry(row)">X</b-button>
          </td>
        </tr>

        <tr class="materials-footer">
          <td colspan="10">
            <b-button class="btn btn-sm" variant="outline-success" @click="addEntry">
              <feather-icon icon="PlusIcon" size="12" /> Add material
            </b-button>
            <small v-if="user.materialEntries.length" class="text-muted ml-1">Saved automatically once job, material, quantity and unit are filled in.</small>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { BButton, BSpinner, VBTooltip } from 'bootstrap-vue';
import vSelect from 'vue-select';
import debounce from 'lodash/debounce';
import store from '@/store';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import JobSelect from './JobSelect.vue';
import { ymd, parseYmd, addDays } from './timesheetDates';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

// Materials one person used this week. Entries feed Reports > Materials Used; they never affect billing.
export default {
  name: 'TimesheetMaterials',
  components: { BButton, BSpinner, vSelect, JobSelect },
  directives: { 'b-tooltip': VBTooltip },

  props: {
    // The timesheet user; this component edits user.materialEntries in place, like the hours rows.
    user: { type: Object, required: true },
    weekEnding: { type: String, required: true },
    jobs: { type: Array, default: () => [] },
    showAllJobs: { type: Boolean, default: false },
    // Each job's part names: [{ jobnumber, partNames }].
    partsByJob: { type: Array, default: () => [] },
    // Active catalogue materials: [{ id, name }].
    catalogue: { type: Array, default: () => [] },
    units: { type: Array, default: () => ['pcs'] },
  },

  computed: {
    // Monday to Saturday of the week, like the hours grid.
    weekDays() {
      const monday = addDays(parseYmd(this.weekEnding), -6);
      return [0, 1, 2, 3, 4, 5].map((offset) => {
        const date = addDays(monday, offset);
        return {
          value: ymd(date),
          label: `${date.toLocaleDateString('en-US', { weekday: 'short' })} ${date.getMonth() + 1}/${date.getDate()}`,
        };
      });
    },
    hasPricedEntries() {
      return this.user.materialEntries.some((row) => this.rowTotal(row) !== null);
    },
    totalCost() {
      return this.user.materialEntries.reduce((total, row) => total + (this.rowTotal(row) || 0), 0);
    },
  },

  created() {
    // Saves for the same entry run one after another, so a new entry is never created twice.
    this.saveQueues = new WeakMap();
    // One debounced save per entry, so typing in two notes quickly saves both.
    this.noteSavers = new WeakMap();
  },

  methods: {
    toast(title, text, variant) {
      this.$toast({ component: ToastificationContent, props: { title, text, icon: 'AlertTriangleIcon', variant } });
    },

    formatMoney(value) {
      return money.format(value);
    },

    // Blank when there is no unit cost, so unpriced entries are never shown as $0.
    rowTotal(row) {
      if (row.unitCost === null || row.unitCost === '' || row.unitCost === undefined) return null;
      const quantity = parseFloat(row.quantity);
      const unitCost = parseFloat(row.unitCost);
      if (Number.isNaN(quantity) || Number.isNaN(unitCost)) return null;
      return Math.round(quantity * unitCost * 100) / 100;
    },

    partOptions(jobId) {
      const job = jobId ? this.partsByJob.find((entry) => entry.jobnumber == jobId) : null;
      return job && Array.isArray(job.partNames) ? job.partNames.filter(Boolean) : [];
    },

    // Bound as an object, so a renamed or retired material still shows its name.
    materialValue(row) {
      return row.materialName ? { id: row.materialId, name: row.materialName } : null;
    },

    setMaterial(row, option) {
      row.materialId = (option && option.id) || null;
      row.materialName = (option && option.name && option.name.trim()) || null;
      this.save(row);
    },

    // Today when it falls in this week, otherwise the nearest day of it.
    defaultDate() {
      const today = ymd(new Date());
      const first = this.weekDays[0].value;
      const last = this.weekDays[this.weekDays.length - 1].value;
      if (today < first) return first;
      if (today > last) return last;
      return today;
    },

    addEntry() {
      this.user.materialEntries.push({
        id: null,
        usedOn: this.defaultDate(),
        jobId: null,
        partName: null,
        materialId: null,
        materialName: null,
        quantity: null,
        unit: this.units.includes('pcs') ? 'pcs' : this.units[0],
        unitCost: null,
        note: null,
        editable: true,
        saveState: null,
      });
    },

    isComplete(row) {
      return !!row.jobId && !!row.materialName && parseFloat(row.quantity) > 0 && !!row.unit && !!row.usedOn;
    },

    finalizedTitle(row) {
      return row.editable
        ? `Billing for job ${row.jobId} is finalized. As a manager you can still change this entry.`
        : `Billing for job ${row.jobId} is finalized, so this entry is locked.`;
    },

    saveStateTitle(row) {
      return {
        saving: 'Saving…',
        saved: 'Saved',
        error: 'Not saved, change a value to try again',
        incomplete: 'Add job, material, quantity and unit to save',
      }[row.saveState] || '';
    },

    debouncedSave(row) {
      if (!this.noteSavers.has(row)) this.noteSavers.set(row, debounce(() => this.save(row), 500));
      this.noteSavers.get(row)();
    },

    errorMessage(error, fallback) {
      const data = error && error.response && error.response.data;
      if (data && data.errors) {
        const first = Object.values(data.errors)[0];
        if (first && first[0]) return first[0];
      }
      return (data && data.message) || fallback;
    },

    save(row) {
      if (!row.editable) return Promise.resolve();
      if (!this.isComplete(row)) {
        this.$set(row, 'saveState', 'incomplete');
        return Promise.resolve();
      }
      this.$set(row, 'saveState', 'saving');

      const run = () => {
        // Built when the save runs, so it carries the id from an earlier save of the same entry.
        const hasCost = row.unitCost !== null && row.unitCost !== '' && row.unitCost !== undefined;
        const payload = {
          id: row.id || null,
          userId: this.user.id,
          weekEnding: this.weekEnding,
          usedOn: row.usedOn,
          jobId: row.jobId,
          partName: row.partName || null,
          materialId: row.materialId || null,
          materialName: row.materialName,
          quantity: parseFloat(row.quantity),
          unit: row.unit,
          unitCost: hasCost ? parseFloat(row.unitCost) : null,
          note: row.note || null,
        };

        return store.dispatch('user/saveTimesheetMaterial', payload).then((response) => {
          const { entry, material } = response.data;
          if (!row.id) this.$set(row, 'id', entry.id);
          row.materialId = entry.materialId;
          row.materialName = entry.materialName;
          row.editable = entry.editable;
          this.$set(row, 'finalized', entry.finalized);
          if (material) this.$emit('material-created', material);
          row.saveState = 'saved';
          setTimeout(() => {
            if (row.saveState === 'saved') row.saveState = null;
          }, 2000);
        }).catch((error) => {
          console.error(error);
          row.saveState = 'error';
          this.toast('Material not saved', this.errorMessage(error, 'This entry could not be saved. Change a value to try again.'), 'danger');
        });
      };

      const queued = (this.saveQueues.get(row) || Promise.resolve()).then(run);
      this.saveQueues.set(row, queued);
      return queued;
    },

    entrySummary(row) {
      const amount = row.quantity ? `${row.quantity} ${row.unit || ''} `.replace(/\s+$/, ' ') : '';
      const what = row.materialName || 'this material';
      const job = row.jobId ? ` (job ${row.jobId})` : '';
      return `${amount}${what}${job}`.trim();
    },

    async deleteEntry(row) {
      const result = await this.$swal({
        title: 'Delete this material?',
        text: `This removes ${this.entrySummary(row)} from ${this.user.name}'s timesheet.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        confirmButtonColor: '#ea5455',
      });
      if (!result.isConfirmed) return;

      // Let any save still in flight finish first, so the entry has its id.
      await (this.saveQueues.get(row) || Promise.resolve());

      if (row.id) {
        try {
          await store.dispatch('user/deleteTimesheetMaterial', { id: row.id });
        } catch (error) {
          console.error(error);
          this.toast('Not deleted', this.errorMessage(error, 'The material could not be deleted.'), 'danger');
          return;
        }
      }
      const index = this.user.materialEntries.indexOf(row);
      if (index !== -1) this.user.materialEntries.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
// Explicit size and alignment: on the Home page the timesheet sits in a large, centered box.
.materials-section {
  margin: -0.5rem 0 2rem;
  font-size: 14px;
  text-align: left;
}

.materials-table {
  margin-bottom: 0;
  border: 1px solid #ebe9f1;
}

.materials-table th,
.materials-table td {
  vertical-align: middle;
  text-align: left;
}

.materials-table .text-right {
  text-align: right;
}

// The app's theme paints table headers dark with white text.
.materials-heading {
  text-transform: none;
  font-size: 13px;
}

.materials-title {
  font-weight: 700;
  margin-right: 8px;
}

.materials-summary {
  font-weight: 400;
  opacity: 0.8;
}

.column-labels th {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.col-date {
  width: 10%;
}

.col-job {
  width: 17%;
}

.col-part {
  width: 11%;
}

.col-material {
  width: 16%;
}

.col-qty {
  width: 6%;
}

.col-unit {
  width: 8%;
}

.col-cost {
  width: 9%;
}

.col-total {
  width: 7%;
}

.col-note {
  width: 11%;
}

.col-actions {
  width: 5%;
}

// Keep a picked value on one line in the narrow cells instead of wrapping under the clear button.
.materials-table ::v-deep .vs__selected-options {
  flex-wrap: nowrap;
  min-width: 0;
}

.materials-table ::v-deep .vs__selected {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ms-input {
  height: auto;
  padding: 2px 4px;
  border: none;
  border-bottom: 1px solid lightgray;
  border-radius: 0;
  background-color: #fff;
  font-size: 13px;
}

select.ms-input {
  min-width: 82px;
  padding-right: 0;
}

.ms-input:focus {
  border-color: #a51c30 !important;
  box-shadow: none;
}

.money-input {
  display: flex;
  align-items: center;
  gap: 2px;
  color: #82868b;
}

.locked td {
  background: #fafafa;
}

.materials-footer td {
  border-top: 1px solid #ebe9f1;
}

.save-state {
  display: inline-block;
  width: 18px;
  margin-right: 6px;
  vertical-align: middle;
  color: #82868b;
}

.save-state.saved {
  color: #28c76f;
}

.save-state.error {
  color: #ea5455;
}

.save-state.incomplete {
  color: #ff9f43;
}

// Amber: finalized but a manager can still edit. Grey: locked.
.finalized-flag {
  display: inline-block;
  margin-right: 4px;
  vertical-align: middle;
  color: #ff9f43;
  cursor: help;
}

.finalized-flag.locked {
  color: #82868b;
}
</style>
